import clsx from "clsx";
import { useTranslation } from "react-i18next";
import React, { ReactElement } from "react";
import { MdDataArray } from "react-icons/md";
import { motion, useReducedMotion } from "framer-motion";
import { forceIconSize } from "src/helpers/icon-utils";
import ButtonGroup from "../ButtonGroup/ButtonGroup";
import { ICON_SIZES } from "src/constants/icons";
import { IButton } from "../Button/Button.types";

const splitTextInLines = (text: string, separator: string = "."): string[] => {
  if (!text) return [];

  const parts = text.split(separator);

  return parts
    .map((line, index) => {
      const trimmed = line.trim();
      if (!trimmed) return "";

      // Si no es el último o el texto original termina con el separador
      const shouldAddSeparator =
        index < parts.length - 1 || text.endsWith(separator);

      return shouldAddSeparator ? trimmed + separator : trimmed;
    })
    .filter(Boolean);
};

interface INoTableData {
  className?: string;
  icon?: ReactElement;
  text?: string;
  buttons?: IButton[];
  animate?: boolean;
}

const NoTableData: React.FC<INoTableData> = ({
  className,
  icon = null,
  text,
  buttons = [],
  animate = false,
}) => {
  const { t } = useTranslation();
  const prefersReducedMotion = useReducedMotion();

  return (
    <motion.div
      className={clsx([
        "flex",
        "w-full",
        "flex-col",
        "gap-2",
        "items-center",
        "justify-center",
        "px-2",
        // "py-2",
        "md:py-[57px]",
        "py-[33px]",
        "border",
        "my-2",
        "rounded",
        "border-gray-200",
        "dark:border-gray-600",
        "border-dashed",
        className,
      ])}
      {...(animate && !prefersReducedMotion
        ? {
            initial: {
              y: 200,
              opacity: 0.5,
            },
            animate: {
              y: 0,
              opacity: 1,
            },
            exit: {
              y: 200,
              opacity: 0.5,
            },
          }
        : {})}
    >
      <motion.div
        {...(!prefersReducedMotion && {
          animate: {
            rotate: [-5, 5, -5],
          },
          transition: {
            duration: 0.6,
            repeat: Infinity,
            repeatType: "loop",
            ease: "easeInOut",
          },
        })}
        style={{ display: "inline-block" }}
        className={clsx(["dark:text-cyan-500", "text-gray-300"])}
      >
        {forceIconSize({
          icon: icon || <MdDataArray />,
          size: ICON_SIZES.XL,
        })}
      </motion.div>

      <div
        className={clsx([
          "text-xs",
          "md:text-xs",
          "dark:text-white/90",
          "text-gray-500",
          "text-center",
        ])}
      >
        {splitTextInLines(text || t("misc:strings.no_elements_to_show")).map(
          (text: string, index: number) => (
            <div key={index}>{text}</div>
          ),
        )}
      </div>
      {buttons?.length > 0 ? <ButtonGroup buttons={buttons} /> : null}
    </motion.div>
  );
};

export default React.memo(NoTableData);
